import { memo, useMemo } from 'react';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';

import { CheckNotificationTypeNamesEnum } from '../enums/CheckNotificationTypeNamesEnum';
import { CheckNotificationTypesEnum } from '../enums/CheckNotificationTypesEnum';
import styles from './CheckNotificationModal.module.scss';

interface ICheckNotificationModalProps {
  visible: boolean;
  onConfirm: () => void;
  notificationType?: CheckNotificationTypesEnum;
}

export const CheckNotificationModal = memo(({ visible, onConfirm, notificationType }: ICheckNotificationModalProps) => {
  const currentYear = new Date().getFullYear();

  const renderNotification = useMemo(() => {
    switch (notificationType) {
      case CheckNotificationTypesEnum.FOR_CREATE_PLAN_WITH_GENERAL_PROSECUTOR: {
        return (
          <>
            <div className={styles.BodyText}>
              В карточке плана в поле «Орган прокуратуры» указана Генеральная прокуратура Российской Федерации, т.е.
              центральный аппарат ведомства.
            </div>
            <div className={styles.BodyText}>Просим Вас удостовериться в правильности данного действия.</div>
          </>
        );
      }
      case CheckNotificationTypesEnum.FOR_CREATE_KNM_WITH_GENERAL_PROSECUTOR: {
        return (
          <>
            <div className={styles.BodyText}>
              В паспорте КНМ в поле «Орган прокуратуры» указана Генеральная прокуратура Российской Федерации, т.е.
              центральный аппарат ведомства.
            </div>
            <div className={styles.BodyText}>Просим Вас удостовериться в правильности данного действия.</div>
            <div className={styles.BodyText}>
              Обращаем внимание, что согласование внеплановых КНМ осуществляется нижестоящими прокуратурами.
            </div>
          </>
        );
      }
      case CheckNotificationTypesEnum.HAS_PLANS_IN_PROCESS: {
        return (
          <>
            <div className={styles.BodyText}>
              Информируем вас, что период подачи ежегодных планов КНМ на будущий год заканчивается 1 октября 23:59
              (МСК).
            </div>
            <div className={styles.BodyText}>
              Система обнаружила у вас ряд планов, которые полностью готовы к согласованию, но ещё не направлены на
              рассмотрение в прокуратуру.
            </div>
            <div className={styles.BodyText}>
              Данные планы были помечены красными пиктограммами рядом с номером плана в вашем списке планов.
            </div>
            <div className={styles.BodyText}>
              Если вы не переведете эти планы в статус «На рассмотрении» до 1 октября 23:59 (МСК) – направление этих
              планов в прокуратуру будет невозможно.
            </div>
            <div className={styles.BodyText}>
              Не рассмотренные планы будут невозможны к утверждению и последующему проведению в будущем году.
            </div>
          </>
        );
      }
      case CheckNotificationTypesEnum.HAS_PLANS_IN_PROCESS_OR_UNDER_CONSIDERATION: {
        return (
          <>
            <div className={styles.BodyText}>
              Информируем вас, что активация системной блокировки перевода планов в статус «Рассмотрено» будет
              осуществлена 1 ноября {currentYear} года.
            </div>
            <div className={styles.BodyText}>
              Система обнаружила у вас ряд планов, которые не были рассмотрены вами.
            </div>
            <div className={styles.BodyText}>
              Данные планы были помечены красными пиктограммами рядом с номером плана в вашем списке планов.
            </div>
            <div className={styles.BodyText}>
              Если вы не переведете эти планы в статус «Рассмотрено» до 1 ноября – данные планы уже не смогут быть
              рассмотрены.
            </div>
            <div className={styles.BodyText}>
              Не рассмотренные планы будут невозможны к утверждению КНО и последующему проведению в&nbsp;
              {currentYear + 1} году.
            </div>
          </>
        );
      }
      case CheckNotificationTypesEnum.HAS_PLANS_NOT_APPROVED: {
        return (
          <>
            <div className={styles.BodyText}>
              Информируем вас, что период утверждения рассмотренных ежегодных планов КНМ на 2022 год заканчивается 15
              декабря 23:59 (МСК)
            </div>
            <div className={styles.BodyText}>
              Система обнаружила у вас ряд планов, которые рассмотрены прокуратурой, но не утверждены вами.
            </div>
            <div className={styles.BodyText}>
              Если вы не переведете эти планы в статус «Утверждено» до 15 декабря – эти планы не смогут быть реализованы
              в будущем году.
            </div>
          </>
        );
      }
      case CheckNotificationTypesEnum.GENERAL_PROSECUTOR_ADDED: {
        return (
          <>
            <div className={styles.BodyText}>
              В Вашем личном кабинете в список связанных организаций добавлена Генеральная прокуратура Российской
              Федерации, т.е. центральный аппарат ведомства.
            </div>
            <div className={styles.BodyText}>Просим Вас удостовериться в правильности данного действия.</div>
            <div className={styles.BodyText}>
              Обращаем внимание, что согласование внеплановых КНМ осуществляется нижестоящими прокуратурами.
            </div>
          </>
        );
      }

      default:
        return null;
    }
  }, [currentYear, notificationType]);

  return (
    <Modal visible={visible} modalBodyClassName={styles.Modal} closeButtonClassName={styles.Close}>
      <ModalHeader title={notificationType && CheckNotificationTypeNamesEnum[notificationType]} sizeText="medium" />
      <ModalBody size="content">
        <div className={styles.SubTitle}>Уважаемый пользователь!</div>
        {renderNotification}
      </ModalBody>
      <ModalActions placement="left">
        <Button text="Подтверждаю ознакомление" color="primary" size="small" onClick={onConfirm} />
      </ModalActions>
    </Modal>
  );
});
